// IndexedDB-Wrapper für die Offline-Fähigkeit (nur Spieltagleiter, nur der nächste Spieltag).
// Bewusst ohne externe Dependency — rohes IndexedDB mit kleinen Promise-Helfern.
//
// Stores:
//   reads (keyPath 'url')   — gecachte GET-Antworten: { url, data, fetchedAt }
//   queue (keyPath 'id', autoIncrement; Index 'eventPlayerId', 'eventId')
//                            — offline Check-ins: { id, eventPlayerId, eventId, presenceStatus,
//                              clientTimestamp, state, attempts, lastError }
//   meta  (keyPath 'key')   — Kleinkram: persistGranted, lastReplayAt, event:{id} (für Lifecycle)

const DB_NAME = 'aveorga-offline';
const DB_VERSION = 1;
export const STORE_READS = 'reads';
export const STORE_QUEUE = 'queue';
export const STORE_META = 'meta';

let dbPromise = null;

export function isSupported() {
  return typeof window !== 'undefined' && 'indexedDB' in window;
}

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (!isSupported()) { reject(new Error('IndexedDB nicht verfügbar')); return; }
    const req = window.indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_READS)) {
        db.createObjectStore(STORE_READS, { keyPath: 'url' });
      }
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        const q = db.createObjectStore(STORE_QUEUE, { keyPath: 'id', autoIncrement: true });
        q.createIndex('eventPlayerId', 'eventPlayerId', { unique: false });
        q.createIndex('eventId', 'eventId', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORE_META)) {
        db.createObjectStore(STORE_META, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function run(storeName, mode, fn) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const t = db.transaction(storeName, mode);
    const store = t.objectStore(storeName);
    let result;
    Promise.resolve(fn(store)).then(r => { result = r; }).catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  }));
}

function reqP(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ---------- reads ----------
export function putRead(url, data) {
  return run(STORE_READS, 'readwrite', store =>
    reqP(store.put({ url, data, fetchedAt: new Date().toISOString() })));
}
export function getRead(url) {
  return run(STORE_READS, 'readonly', store => reqP(store.get(url)));
}
export function deleteRead(url) {
  return run(STORE_READS, 'readwrite', store => reqP(store.delete(url)));
}
export function clearReads() {
  return run(STORE_READS, 'readwrite', store => reqP(store.clear()));
}

// ---------- meta ----------
export function putMeta(key, value) {
  return run(STORE_META, 'readwrite', store => reqP(store.put({ key, ...value })));
}
export function getMeta(key) {
  return run(STORE_META, 'readonly', store => reqP(store.get(key)));
}
export function deleteMeta(key) {
  return run(STORE_META, 'readwrite', store => reqP(store.delete(key)));
}
export function getAllMeta() {
  return run(STORE_META, 'readonly', store => reqP(store.getAll()));
}

// ---------- queue (Check-ins; genutzt ab M4/M5) ----------
export function addToQueue(item) {
  return run(STORE_QUEUE, 'readwrite', store => reqP(store.add(item)));
}
export function putQueueItem(item) {
  return run(STORE_QUEUE, 'readwrite', store => reqP(store.put(item)));
}
export function deleteQueueItem(id) {
  return run(STORE_QUEUE, 'readwrite', store => reqP(store.delete(id)));
}
export function getQueueItem(id) {
  return run(STORE_QUEUE, 'readonly', store => reqP(store.get(id)));
}
export function getAllQueue() {
  return run(STORE_QUEUE, 'readonly', store => reqP(store.getAll()));
}
export function getQueueByEvent(eventId) {
  return run(STORE_QUEUE, 'readonly', store =>
    reqP(store.index('eventId').getAll(Number(eventId))));
}
export function getQueueByEventPlayer(eventPlayerId) {
  return run(STORE_QUEUE, 'readonly', store =>
    reqP(store.index('eventPlayerId').getAll(Number(eventPlayerId))));
}

// Coalesce-Upsert: pro eventPlayerId überlebt nur die jeweils letzte Absicht.
// Vorhandene Zeilen desselben Spielers werden ersetzt (presence ist set-to-value/idempotent).
// Lesen + Ersetzen in EINER Transaktion (atomar, auch bei schnellen Doppel-Taps). Achtung: innerhalb
// der Transaktion NICHT auf Nicht-IDB-Promises awaiten (sonst committed sie vorzeitig) — daher nur
// Request-Callbacks. Liefert die ID der neuen Zeile.
export function upsertQueuePresence(entry) {
  return run(STORE_QUEUE, 'readwrite', (store) => new Promise((resolve, reject) => {
    const keysReq = store.index('eventPlayerId').getAllKeys(Number(entry.eventPlayerId));
    keysReq.onsuccess = () => {
      for (const key of (keysReq.result || [])) store.delete(key);
      const addReq = store.add(entry);
      addReq.onsuccess = () => resolve(addReq.result);
      addReq.onerror = () => reject(addReq.error);
    };
    keysReq.onerror = () => reject(keysReq.error);
  }));
}

// --- Lifecycle ---

// Beim expliziten Logout: gecachte Listen (PII) + Event-Meta entfernen. Die Queue bleibt erhalten,
// damit offline erfasste, noch nicht übertragene Check-ins nicht verloren gehen.
export async function clearOnLogout() {
  if (!isSupported()) return;
  try {
    await clearReads();
    const metas = (await getAllMeta()) || [];
    for (const m of metas) {
      if (m.key && m.key.indexOf('event:') === 0) await deleteMeta(m.key);
    }
  } catch (e) { /* best effort */ }
}

// Persistenten Speicher anfordern (reduziert Eviction-Risiko, v.a. iOS). Best-effort.
export async function requestPersistentStorage() {
  if (!isSupported()) return;
  try {
    if (navigator.storage && navigator.storage.persist) {
      const granted = await navigator.storage.persist();
      await putMeta('persistGranted', { value: !!granted });
    }
  } catch (e) { /* best effort */ }
}
